/**
 * Discount Calculation Utilities
 * Handles complex discount logic including category-based, product-specific, and conditional discounts
 */

export interface DiscountCode {
  id: string;
  code: string;
  type: 'percentage' | 'fixed' | 'conditional';
  value: number | null;
  base_value: number | null;
  bonus_value: number | null;
  applicable_to: 'website' | 'marketing' | 'both';
  applies_to_services: string[] | null;
  applies_to_products: string[] | null;
  requires_products: string[] | null;
  discount_behavior: 'standalone' | 'conditional' | null;
  min_spend: number | null;
  max_discount: number | null;
  waive_setup: boolean;
  condition: string | null;
  active: boolean;
}

export interface ProductBreakdown {
  category: string;
  tier_key: string;
  price: number;
  name?: string;
}

export interface DiscountCalculationResult {
  totalDiscount: number;
  itemizedDiscounts: Map<string, number>;
  setupWaived: boolean;
  appliedCategories: string[];
}

/**
 * Maps database category names to calculator breakdown keys
 */
function mapCategoryToBreakdownKey(category: string): string {
  const mapping: Record<string, string> = {
    'ad_platforms': 'ppc',
    'seo_services': 'seo',
    'email_services': 'email',
    'cro_services': 'cro',
    'support_plans': 'support_plans',
    'website_packages': 'website_packages',
    'web_addons': 'web_addons',
  };
  return mapping[category] || category;
}

/**
 * Check if discount applies to a specific service category
 */
export function canApplyToCategory(
  discount: DiscountCode,
  category: string
): boolean {
  // If no specific services defined, apply based on applicable_to
  if (!discount.applies_to_services || discount.applies_to_services.length === 0) {
    return true;
  }

  // Check both the original category and the mapped breakdown key
  const breakdownKey = mapCategoryToBreakdownKey(category);
  return (
    discount.applies_to_services.includes(category) ||
    discount.applies_to_services.includes(breakdownKey)
  );
}

/**
 * Check if discount applies to a specific product
 */
export function canApplyToProduct(
  discount: DiscountCode,
  productKey: string
): boolean {
  // If no specific products defined, not a product-specific discount
  if (!discount.applies_to_products || discount.applies_to_products.length === 0) {
    return false;
  }

  return discount.applies_to_products.includes(productKey);
}

/**
 * Check if required products condition is met
 */
export function meetsProductRequirements(
  discount: DiscountCode,
  selectedProducts: string[]
): boolean {
  // If no requirements, condition is met
  if (!discount.requires_products || discount.requires_products.length === 0) {
    return true;
  }

  // Check if all required products are selected
  return discount.requires_products.every(req => selectedProducts.includes(req));
}

/**
 * Calculate discount amount for a single item
 */
export function calculateItemDiscount(
  discount: DiscountCode,
  itemPrice: number,
  itemCategory?: string,
  itemKey?: string
): number {
  // Check minimum spend
  if (discount.min_spend && itemPrice < discount.min_spend) {
    return 0;
  }

  // Calculate base discount
  let discountAmount = 0;

  if (discount.type === 'percentage' && discount.value) {
    discountAmount = itemPrice * (discount.value / 100);
  } else if (discount.type === 'fixed' && discount.value) {
    discountAmount = discount.value;
  } else if (discount.type === 'conditional') {
    // Conditional discounts need special handling at the total level
    return 0;
  }

  // Apply maximum discount limit
  if (discount.max_discount && discountAmount > discount.max_discount) {
    discountAmount = discount.max_discount;
  }

  // Ensure discount doesn't exceed item price
  return Math.min(discountAmount, itemPrice);
}

/**
 * Calculate total discount for web services with granular category filtering
 */
export function calculateWebDiscount(
  discount: DiscountCode,
  breakdown: ProductBreakdown[],
  selectedProductKeys: string[]
): DiscountCalculationResult {
  const result: DiscountCalculationResult = {
    totalDiscount: 0,
    itemizedDiscounts: new Map(),
    setupWaived: false,
    appliedCategories: [],
  };

  // Check if this is a product-specific discount
  const isProductSpecific =
    discount.applies_to_products && discount.applies_to_products.length > 0;

  // Check product requirements
  if (!meetsProductRequirements(discount, selectedProductKeys)) {
    return result;
  }

  // If product-specific, only apply to matching products
  if (isProductSpecific) {
    breakdown.forEach(item => {
      if (canApplyToProduct(discount, item.tier_key)) {
        const itemDiscount = calculateItemDiscount(
          discount,
          item.price,
          item.category,
          item.tier_key
        );
        if (itemDiscount > 0) {
          result.itemizedDiscounts.set(item.tier_key, itemDiscount);
          result.totalDiscount += itemDiscount;
          if (!result.appliedCategories.includes(item.category)) {
            result.appliedCategories.push(item.category);
          }
        }
      }
    });
  } else {
    // Category-based discount
    breakdown.forEach(item => {
      if (canApplyToCategory(discount, item.category)) {
        const itemDiscount = calculateItemDiscount(
          discount,
          item.price,
          item.category,
          item.tier_key
        );
        if (itemDiscount > 0) {
          result.itemizedDiscounts.set(item.tier_key, itemDiscount);
          result.totalDiscount += itemDiscount;
          if (!result.appliedCategories.includes(item.category)) {
            result.appliedCategories.push(item.category);
          }
        }
      }
    });
  }

  // Check if total meets minimum spend
  if (discount.min_spend && result.totalDiscount > 0) {
    const totalBeforeDiscount = breakdown.reduce((sum, item) => sum + item.price, 0);
    if (totalBeforeDiscount < discount.min_spend) {
      return {
        totalDiscount: 0,
        itemizedDiscounts: new Map(),
        setupWaived: false,
        appliedCategories: [],
      };
    }
  }

  return result;
}

/**
 * Calculate discount for marketing services with special handling for conditional discounts
 */
export function calculateMarketingDiscount(
  discount: DiscountCode,
  breakdown: ProductBreakdown[],
  selectedProductKeys: string[],
  adPlatformCount: number = 0
): DiscountCalculationResult {
  const result: DiscountCalculationResult = {
    totalDiscount: 0,
    itemizedDiscounts: new Map(),
    setupWaived: discount.waive_setup && adPlatformCount >= 2,
    appliedCategories: [],
  };

  // Check product requirements
  if (!meetsProductRequirements(discount, selectedProductKeys)) {
    return result;
  }

  // Handle conditional discounts (e.g., based on number of ad platforms)
  if (discount.type === 'conditional') {
    let conditionalDiscount = 0;
    if (adPlatformCount === 2 && discount.base_value) {
      conditionalDiscount = discount.base_value;
    } else if (adPlatformCount >= 3 && discount.bonus_value) {
      conditionalDiscount = discount.bonus_value;
    }

    if (conditionalDiscount > 0) {
      result.totalDiscount = conditionalDiscount;
      // Distribute discount proportionally across applicable items
      const totalPrice = breakdown.reduce((sum, item) => sum + item.price, 0);
      breakdown.forEach(item => {
        if (canApplyToCategory(discount, item.category)) {
          const proportion = item.price / totalPrice;
          const itemDiscount = conditionalDiscount * proportion;
          result.itemizedDiscounts.set(item.tier_key, itemDiscount);
          if (!result.appliedCategories.includes(item.category)) {
            result.appliedCategories.push(item.category);
          }
        }
      });
    }
    return result;
  }

  // Check if this is a product-specific discount
  const isProductSpecific =
    discount.applies_to_products && discount.applies_to_products.length > 0;

  // Calculate discounts
  if (isProductSpecific) {
    breakdown.forEach(item => {
      if (canApplyToProduct(discount, item.tier_key)) {
        const itemDiscount = calculateItemDiscount(
          discount,
          item.price,
          item.category,
          item.tier_key
        );
        if (itemDiscount > 0) {
          result.itemizedDiscounts.set(item.tier_key, itemDiscount);
          result.totalDiscount += itemDiscount;
          if (!result.appliedCategories.includes(item.category)) {
            result.appliedCategories.push(item.category);
          }
        }
      }
    });
  } else {
    // Category-based discount
    breakdown.forEach(item => {
      if (canApplyToCategory(discount, item.category)) {
        const itemDiscount = calculateItemDiscount(
          discount,
          item.price,
          item.category,
          item.tier_key
        );
        if (itemDiscount > 0) {
          result.itemizedDiscounts.set(item.tier_key, itemDiscount);
          result.totalDiscount += itemDiscount;
          if (!result.appliedCategories.includes(item.category)) {
            result.appliedCategories.push(item.category);
          }
        }
      }
    });
  }

  // Apply max discount limit to total
  if (discount.max_discount && result.totalDiscount > discount.max_discount) {
    // Scale down itemized discounts proportionally
    const scale = discount.max_discount / result.totalDiscount;
    result.itemizedDiscounts.forEach((value, key) => {
      result.itemizedDiscounts.set(key, value * scale);
    });
    result.totalDiscount = discount.max_discount;
  }

  // Check if total meets minimum spend
  if (discount.min_spend && result.totalDiscount > 0) {
    const totalBeforeDiscount = breakdown.reduce((sum, item) => sum + item.price, 0);
    if (totalBeforeDiscount < discount.min_spend) {
      return {
        totalDiscount: 0,
        itemizedDiscounts: new Map(),
        setupWaived: false,
        appliedCategories: [],
      };
    }
  }

  return result;
}
